<script>
import { formatNumber } from '@ubic/utils/formatters';
import IconGhost from '@/components/icons/Ghost';

const d3 = require('d3-shape');

const MAX_RADIUS = 180;

export default {
  props: {
    filter: {
      type: Object,
      default: () => ({}),
    },
    dataService: {
      type: Function,
      required: true,
    },
  },
  data () {
    return {
      isFetching: false,
      data: [],
      totalHours: 0,

      width: 0,
    };
  },
  computed: {
    radius () {
      return Math.min(MAX_RADIUS, this.width / 2);
    },
    arc () {
      const { radius } = this;

      return d3.arc()
        .innerRadius(radius - 40)
        .outerRadius(radius);
    },
    infoArc () {
      const { radius } = this;

      return d3.arc()
        .innerRadius(0)
        .outerRadius(radius);
    },
    pie () {
      return (d3.pie()
        .sort(null)
        .value(d => d.totalHours)
      )(this.data);
    },
  },
  watch: {
    filter () {
      this.fetchData();
    },
  },
  methods: {
    async fetchData () {
      const response = await this.dataService(this.filter);
      this.data = Object.freeze(response.data);
      this.totalHours = response.meta.totalHours;
    },
  },
  async created () {
    this.isFetching = true;

    await this.fetchData();

    this.isFetching = false;
  },
  mounted () {
    const { width } = this.$el.getBoundingClientRect();

    this.width = width;
  },
  render () {
    if (this.isFetching || !this.width) {
      return (
        <div>
          <u-spinner />
        </div>
      );
    }

    const { $style, radius, arc, infoArc, pie } = this;

    return (
      <div class={$style.wrapper}>
        {!pie.length && (
          <div class={$style.empty}>
            <IconGhost />
          </div>
        )}

        <svg
          viewBox={`0 0 ${2 * radius} ${2 * radius}`}
          width={2 * radius}
          height={2 * radius}
        >
          <g transform={`translate(${radius}, ${radius})`}>
            <circle
              cx="0"
              cy="0"
              r={radius}
              class={$style.background}
            />
            <g class={$style.info}>
              {pie.map(p => {
                const [outerX, outerY] = arc.centroid(p);

                return (
                  <g
                    key={p.data.name}
                    class={$style.infoArc}
                  >
                    <g
                      class={$style.infoArcBackground}
                      style={{ stroke: p.data.color }}
                    >
                      <line
                        x1="0"
                        y1="0"
                        x2={outerX}
                        y2={outerY}
                      />
                      <circle
                        cx="0"
                        cy="0"
                        r="80"
                      />
                    </g>

                    <text class={$style.infoArcHours}>
                      {formatNumber(p.data.totalHours, { round: 2 })}
                      ч
                    </text>
                    <text
                      dy="1.35em"
                      class={$style.infoArcName}
                    >
                      {p.data.name}
                    </text>

                    <path
                      d={infoArc(p)}
                      fill="transparent"
                    />
                  </g>
                );
              })}
            </g>
            <g class={$style.chart}>
              {pie.map(p => (
                <path
                  key={p.data.name}
                  d={arc(p)}
                  fill={p.data.color}
                >
                  <title>
                    {p.data.name}
                  </title>
                </path>
              ))}
            </g>
          </g>
        </svg>
      </div>
    );
  },
};
</script>

<style lang="scss" module>
@import '@/theme/include.scss';

.wrapper {
  margin: 0 auto;
  position: relative;
}

.empty {
  color: $color-gray-lighter;
  font-size: 6em;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
}

.background {
  fill: $color-gray-lightest;
}

.info {
  &Arc {
    opacity: 0;

    &:first-child {
      opacity: 1;
    }

    &:hover {
      opacity: 1;
    }

    &Background {
      fill: $color-white;
      stroke-width: 2;
    }

    &Name,
    &Hours {
      text-anchor: middle;
    }

    &Hours {
      font-size: 1.5em;
      font-weight: bold;
    }
  }

  &:hover &Arc:not(:hover) {
    &,
    &:first-child {
      opacity: 0;
    }
  }
}

.chart {
  pointer-events: none;
}
</style>
